/// <reference lib="webworker" />

import { isPlainObject } from "lodash-es";

type BaseWorkerEventMap = {
  [key in string]: {
    input: unknown;
    output: unknown;
  };
};

export class WorkerClient<EventMap extends BaseWorkerEventMap> {
  private worker?: Worker;

  private workerEventId = 1;

  /** Rejects in-flight events, keyed by event id */
  private pendingRejects = new Map<number, (error: Error) => void>();

  /**
   * Set when the worker itself dies (failed to load, uncaught error). Without
   * this, every event posted to a dead worker would hang forever.
   */
  private workerError?: Error;

  constructor(private readonly createWorker: () => Worker) {}

  public get initialized(): boolean {
    return !!this.worker;
  }

  postWorkerEvent<T extends keyof EventMap>(
    type: T,
    data: EventMap[T]["input"],
  ): Promise<EventMap[T]["output"]> {
    if (this.workerError) return Promise.reject(this.workerError);

    if (!this.worker) {
      this.worker = this.createWorker();
      this.worker.addEventListener("error", (event) => {
        this.handleWorkerError(
          new Error(event.message || "Worker failed to load"),
        );
      });
    }
    const worker = this.worker;

    const id = this.workerEventId++;
    return new Promise((resolve, reject) => {
      const listener = (
        event: MessageEvent<{
          id: number;
          error?: string;
          result?: EventMap[T]["output"];
        }>,
      ) => {
        const data = event.data;
        if (data.id === id) {
          worker.removeEventListener("message", listener);
          this.pendingRejects.delete(id);
          if (data.error) {
            reject(new Error(data.error));
          } else {
            resolve(data.result!);
          }
        }
      };
      this.pendingRejects.set(id, (error) => {
        worker.removeEventListener("message", listener);
        reject(error);
      });
      worker.addEventListener("message", listener);
      worker.postMessage({ id, type, data });
    });
  }

  private handleWorkerError(error: Error): void {
    this.workerError = error;
    for (const reject of this.pendingRejects.values()) reject(error);
    this.pendingRejects.clear();
  }
}

export class WorkerServer<EventMap extends BaseWorkerEventMap> {
  constructor(private readonly workerSelf: DedicatedWorkerGlobalScope) {}

  private handlers: {
    [K in keyof EventMap]?: (
      data: EventMap[K]["input"],
    ) => EventMap[K]["output"];
  } = {};

  public addHandler<T extends keyof EventMap>(
    type: T,
    handler: (data: EventMap[T]["input"]) => EventMap[T]["output"],
  ) {
    this.handlers[type] = handler;
    return this;
  }

  public init(): void {
    this.workerSelf.addEventListener("message", (event) => {
      const { id, type, data } = event.data as {
        id: number;
        type: keyof EventMap;
        data: EventMap[keyof EventMap]["input"];
      };
      if (typeof id !== "number" || typeof type !== "string") {
        throw new Error("Invalid worker event data");
      }
      try {
        const handler = this.handlers[type];
        if (!handler) {
          throw new Error(`Handler for event ${type} not found`);
        }

        const result = handler(data);
        this.workerSelf.postMessage({ id, result }, findBuffers(result));
      } catch (err) {
        console.error(`Error handling event ${type} id=${id}:`, err);
        this.workerSelf.postMessage({
          id,
          error:
            err instanceof Error
              ? err.message
              : (err?.toString() ?? "Unknown error"),
        });
      }
    });
  }
}

/**
 * Find all array-buffers, like from Uint8Array, Int8Array, etc.
 */
function findBuffers(obj: unknown, out: ArrayBuffer[] = []): ArrayBuffer[] {
  if (obj == null) return out;

  if (obj instanceof ArrayBuffer) {
    out.push(obj);
  } else if (ArrayBuffer.isView(obj)) {
    // SharedArrayBuffer is not transferable, so it must not be collected here
    if (obj.buffer instanceof ArrayBuffer) out.push(obj.buffer);
  } else if (isPlainObject(obj)) {
    for (const key in obj) {
      findBuffers(obj[key as keyof typeof obj], out);
    }
  } else if (Array.isArray(obj)) {
    for (const item of obj) {
      findBuffers(item, out);
    }
  }

  return out;
}
